import { AppRoute } from './common';

// The Mixpanel group key used for dealership-level group analytics.
export const DEALERSHIP_NAME_GROUP_KEY = 'dealership_name';

// Normalized route patterns for Mixpanel event tracking.
export const ROUTE_PATTERNS = Object.values(AppRoute);

/**
 * Single source of truth for Mixpanel event names.
 *
 * NOTE: the following dynamic/templated event names are NOT yet represented here
 * because they build their name at runtime and need refactoring to a static name
 * plus a property first: `New ${trip.type}`, `Unlink ${trip.type}`,
 * `Unlink ${trip.type} confirm`.
 */
export const ANALYTICS_EVENTS = {
  // --- Agreement lifecycle ---
  NEW_AGREEMENT_MODAL: 'New Agreement Modal',
  CREATE_AGREEMENT: 'Create Agreement',
  CREATE_NEW_AGREEMENT: 'Create New Agreement', // propbably to remove
  CONTINUE_TO_AGREEMENT: 'Continue to Agreement',
  RESERVE_VEHICLE: 'Reserve Vehicle',
  GO_TO_AGREEMENT: 'Go to Agreement',
  OPEN_AGREEMENT: 'Open Agreement',
  OPEN_NOW: 'Open Now',
  OPEN_BACKDATED: 'Open Backdated',
  MARK_AS_READY: 'Mark As Ready',
  REOPEN_AGREEMENT: 'Reopen Agreement',
  CLOSE_AGREEMENT: 'Close Agreement',
  DELETE_AGREEMENT: 'Delete Agreement',
  PRINT: 'Print',
  CREATE_NEW_CUSTOMER: 'Create New Customer',

  // --- Signatures ---
  E_SIGN: 'E-Sign',
  CHECK_IN_SIGNATURE_BUTTON_CLICKED: 'Check-In Signature Button Clicked',
  GET_NEW_E_SIGNATURE: 'Get New E-Signature',
  SEND_COPY: 'Send Copy',
  SEND_COPY_NOW: 'Send Copy Now',
  CHECK_NOW: 'Check Now',

  // --- Agreement details / inspection fields ---
  ADVISOR_ID_CHANGE: 'Advisor ID Change',
  AGREEMENT_NOTES_CHANGE: 'Agreement Notes Change',
  INTERNAL_NOTES_CHANGE: 'Internal Notes Change',
  REFRESH_CONNECTED_VALUE: 'Refresh Connected Value',
  FUEL_IN_CHANGE: 'Fuel In Change',
  ODOMETER_IN_CHANGE: 'Odometer In Change',
  BATTERY_LEVEL_IN_CHANGE: 'Battery Level In Change',
  FUEL_OUT_CHANGE: 'Fuel Out Change',
  ODOMETER_OUT_CHANGE: 'Odometer Out Change',
  BATTERY_LEVEL_OUT_CHANGE: 'Battery Level Out Change',
  MODIFY: 'Modify',
  SET_TO_NOW: 'Set To Now',
  ADDITIONAL_DRIVER: 'Additional Driver',
  BILL_TO_ENABLED: 'Bill-to Enabled',
  BILL_TO_DISABLED: 'Bill-to Disabled',

  // --- Customer check-in ---
  CUSTOMER_CHECKIN_REQUEST_SENT: 'Customer Checkin Request Sent',
  CUSTOMER_CHECKIN_REQUEST_START: 'Customer Checkin Request Start',

  // --- Payments ---
  PAY: 'Pay',
  CONFIRM_PAY: 'Confirm Pay',
  CONFIRM_PAYMENT: 'Confirm Payment',
  SENT_PAYMENT_DETAILS_REQUEST: 'Sent Payment Details Request',
  REQUEST_PAYMENT_DETAILS_MODAL_OPENED: 'Request Payment Details Modal Opened',
  REQUEST_PAYMENT_DETAILS_MODAL_CLOSED: 'Request Payment Details Modal Closed',
  SUBMITTED_CREDIT_CARD_DETAILS_MANUALLY: 'Submitted Credit Card Details Manually',
  ADD_CARD_CONTACTLESS: 'Add Card Contactless',
  ADD_CARD_MANUAL: 'Add Card Manual',
  UPDATE_CARD_CONTACTLESS: 'Update Card Contactless',
  UPDATE_CARD_MANUAL: 'Update Card Manual',

  // --- Vehicles ---
  ADD_TO_FLEET: 'Add to Fleet',
  MAP: 'Map',
  ADD_VEHICLE_NEXT: 'Next (New Vehicle Modal)',

  // --- Damage ---
  ADD_DAMAGE: 'Add Damage',
  DAMAGE_PICTURE_ADDED: 'Damage picture added',

  // --- Planner ---
  NEXT_WEEK: 'Next Week',
  PREVIOUS_WEEK: 'Previous Week',
  TODAY_LINK: 'Today Link',
  WEEK_SELECTOR: 'Week Selector', // Why are we doing this one? // to remove
  SWITCH_TO_TIMELINE_VIEW: 'Switch to Timeline View',
  SWITCH_TO_BOARD_VIEW: 'Switch to Board View',
  PLANNER_VEHICLE_LINK: 'Planner Vehicle Link',
  PLANNER_AGREEMENT_LINK: 'Planner Agreement Link',
  AGREEMENT_BLOCK_CARD_BOARD: 'Agreement Block Card - Board',
  AGREEMENT_BLOCK_CARD_TIMELINE: 'Agreement Block Card - Timeline',
  NEW_AGREEMENT_ICON: 'New Agreement Icon',
  NEW_AGREEMENT_BLOCK: 'New Agreement Block',

  // --- Appointments ---
  BOOK_APPOINTMENT: 'Book Appointment',
  APPOINTMENT_DISMISSED: 'Appointment Dismissed',
  APPOINTMENT_BLOCK_CARD: 'Appointment Block Card',
  CONVERT_APPOINTMENT_TO_AGREEMENT: 'Convert Appointment to Agreement',
  AGREEMENT_CREATED_FROM_APPOINTMENT: 'Agreement Created from Appointment',

  // --- Valet ---
  VALET_EDIT: 'Edit',
  VALET_COMPLETE: 'Complete',
  VALET_DELETE: 'Delete',
  LINK_EXISTING_AGREEMENT: 'Link Existing Agreement',
  UNLINK_AGREEMENT: 'Unlink agreement',
  CUSTOMER_CHANGE: 'Customer Change',
  SAME_AS_ADDRESS: 'Same as address',

  // --- Customers ---
  ARCHIVE_CUSTOMER: 'Archive Customer',
  CUSTOMER_ACTIONS: 'Customer Actions',

  // --- Geofencing ---
  GEOFENCE_CREATED: 'Geofence Created',
  GEOFENCE_UPDATED: 'Geofence Updated',
  GEOFENCE_ALERT_ON_ENTRY_ENABLED: 'Geofence Alert On Entry Enabled',
  GEOFENCE_ALERT_ON_ENTRY_DISABLED: 'Geofence Alert On Entry Disabled',
  GEOFENCE_ALERT_ON_EXIT_ENABLED: 'Geofence Alert On Exit Enabled',
  GEOFENCE_ALERT_ON_EXIT_DISABLED: 'Geofence Alert On Exit Disabled',
  EDIT_GEOFENCE_MODAL_OPENED: 'Edit Geofence Modal Opened',
  NEW_GEOFENCE_MODAL_OPENED: 'New Geofence Modal Opened',

  // --- Fleet settings -> Payment ---
  CONTACTLESS_PAYMENT_ENABLED: 'Contactless Payment: Enabled',
  CONTACTLESS_PAYMENT_DISABLED: 'Contactless Payment: Disabled',
  ALLOW_CC_MANUAL_ENTRY_ENABLED: 'Allow CC Manual Entry: Enabled',
  ALLOW_CC_MANUAL_ENTRY_DISABLED: 'Allow CC Manual Entry: Disabled',

  // --- Telematics ---
  ASSIGN_VIN_TRIGGERED: 'Assign VIN was triggered',

  // --- User management ---
  CREATE_ACCOUNT: 'Create Account',
  EDIT_USER_ACCOUNT: 'Edit User Account',
  DELETE_ACCOUNT: 'Delete Account',

  // --- Export / data / tables ---
  EXPORT: 'Export',
  EXPORT_MODAL_OPENED: 'Export Modal Opened',
  SEARCH: 'Search',
  VIEWS_DROPDOWN: 'Views Dropdown',

  // --- Navigation ---
  PAGE_VIEW: 'Page View',

  // --- Dashboard ---
  ACTIVE_FLEET_CARD_VIEW_CLICKED: 'Active Fleet Card View Clicked',
  BLOCKER_CARD_VIEW_CLICKED: 'Blocker Card View Clicked',
  DATE_RANGE_SLIDER_RANGE_SELECTED: 'Date Range Slider - Range Selected',
  DATE_RANGE_SLIDER_APPLIED_CUSTOM_RANGE: 'Date Range Slider - Applied Custom Range',
  EXCLUDE_WEEKENDS_TOGGLED: 'Exclude Weekends Toggled',
  EXCLUDE_SHUTTLE_TOGGLED: 'Exclude Shuttle Toggled',

  // --- Generic UI ---
  CLOSE_MODAL: 'Close Modal',
  LOG_OUT: 'Log Out',
  ACTIONS: 'Actions',
  CANCEL: 'Cancel',
  BACK: 'Back',
  CONFIRM: 'Confirm',
  SAVE: 'Save',
} as const;
